import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getTeamSessionsInRange, type ClubSession } from "../../services/api";
import { dayKey, monthGrid } from "../../lib/monthGrid";

/**
 * Calendarul antrenamentelor unei echipe, pe luni.
 *
 * The club's own calendar answers "is the hall free"; this one answers "how is
 * this team's week laid out", which is the question somebody has while looking
 * at the team. A month at a time, because a training plan is a rhythm — three
 * evenings a week with a gap over the holidays — and that shape is invisible in
 * a list sorted by date.
 *
 * Past trainings are drawn faded and marked when the register was taken, so an
 * empty week reads as "nothing happened" rather than "nothing was planned".
 */

const DAY_HEADERS = ["Lu", "Ma", "Mi", "Jo", "Vi", "Sâ", "Du"];
const MONTHS = [
  "ianuarie", "februarie", "martie", "aprilie", "mai", "iunie",
  "iulie", "august", "septembrie", "octombrie", "noiembrie", "decembrie",
];

const hhmm = (iso: string) =>
  new Date(iso).toLocaleTimeString("ro-RO", { hour: "2-digit", minute: "2-digit" });

export default function TeamTrainingsCalendar({
  teamId,
  teamName,
  onClose,
  onAddForDay,
}: {
  teamId: string;
  teamName: string;
  onClose: () => void;
  /** Lets the calendar hand a date straight to the add-training form. */
  onAddForDay?: (isoDate: string) => void;
}) {
  const today = new Date();
  const [cursor, setCursor] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));
  const [sessions, setSessions] = useState<ClubSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selected, setSelected] = useState<string | null>(dayKey(today));

  const cells = useMemo(() => monthGrid(cursor.getFullYear(), cursor.getMonth()), [cursor]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      // The whole grid, not just the month: the leading and trailing days are
      // on screen and an empty cell there would be a lie.
      const from = new Date(cells[0].getFullYear(), cells[0].getMonth(), cells[0].getDate(), 0, 0, 0);
      const lastCell = cells[cells.length - 1];
      const to = new Date(lastCell.getFullYear(), lastCell.getMonth(), lastCell.getDate(), 23, 59, 59);
      setSessions(await getTeamSessionsInRange(teamId, from.toISOString(), to.toISOString()));
    } catch {
      setError(true);
      setSessions([]);
    }
    setLoading(false);
  }, [teamId, cells]);

  useEffect(() => {
    load();
  }, [load]);

  const byDay = useMemo(() => {
    const map = new Map<string, ClubSession[]>();
    for (const s of sessions) {
      const key = dayKey(new Date(s.start_at));
      const list = map.get(key) || [];
      list.push(s);
      map.set(key, list);
    }
    map.forEach((list) => list.sort((a, b) => a.start_at.localeCompare(b.start_at)));
    return map;
  }, [sessions]);

  const step = (months: number) =>
    setCursor((c) => new Date(c.getFullYear(), c.getMonth() + months, 1));

  const selectedSessions = selected ? byDay.get(selected) || [] : [];
  const todayKey = dayKey(today);
  const monthTotal = sessions.filter(
    (s) => new Date(s.start_at).getMonth() === cursor.getMonth()
  ).length;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto bg-black/50">
      <div className="w-full max-w-3xl my-8 bg-white shadow-xl rounded-2xl">
        <div className="flex flex-wrap items-center gap-3 px-5 py-4 border-b border-slate-100">
          <div className="min-w-0 mr-auto">
            <h3 className="font-bold text-slate-800">Calendar antrenamente</h3>
            <p className="text-xs text-slate-500">
              {teamName} · {monthTotal} în {MONTHS[cursor.getMonth()]}
            </p>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => step(-1)}
              className="p-2 rounded-lg text-slate-600 hover:bg-slate-100"
              aria-label="Luna anterioară"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={() => setCursor(new Date(today.getFullYear(), today.getMonth(), 1))}
              className="px-3 py-1.5 text-sm font-semibold text-indigo-600 rounded-lg hover:bg-indigo-50"
            >
              Azi
            </button>
            <button
              onClick={() => step(1)}
              className="p-2 rounded-lg text-slate-600 hover:bg-slate-100"
              aria-label="Luna următoare"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
          <span className="text-sm font-semibold capitalize text-slate-700 whitespace-nowrap">
            {MONTHS[cursor.getMonth()]} {cursor.getFullYear()}
          </span>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-slate-400 hover:bg-slate-100"
            aria-label="Închide"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-3 sm:p-5">
          {error ? (
            <div className="py-10 text-center">
              <p className="text-sm text-slate-500">Nu am putut încărca antrenamentele.</p>
              <button onClick={load} className="mt-2 text-sm font-semibold text-indigo-600">
                Reîncearcă
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-7 gap-1 mb-1">
                {DAY_HEADERS.map((d) => (
                  <div key={d} className="py-1 text-xs font-bold text-center text-slate-400">
                    {d}
                  </div>
                ))}
              </div>
              <div className={`grid grid-cols-7 gap-1 ${loading ? "opacity-50" : ""}`}>
                {cells.map((cell) => {
                  const key = dayKey(cell);
                  const inMonth = cell.getMonth() === cursor.getMonth();
                  const list = byDay.get(key) || [];
                  const isToday = key === todayKey;
                  const isSelected = key === selected;
                  const past = key < todayKey;
                  return (
                    <button
                      key={key}
                      onClick={() => setSelected(key)}
                      className={`min-h-[62px] sm:min-h-[76px] p-1 text-left align-top border rounded-lg transition-colors ${
                        isSelected
                          ? "border-indigo-500 bg-indigo-50/70"
                          : isToday
                            ? "border-indigo-200 bg-indigo-50/40"
                            : "border-slate-200 hover:bg-slate-50"
                      } ${inMonth ? "" : "opacity-45"}`}
                    >
                      <span
                        className={`block text-[11px] font-bold ${
                          isToday ? "text-indigo-700" : "text-slate-500"
                        }`}
                      >
                        {cell.getDate()}
                      </span>
                      <span className="block mt-0.5 space-y-0.5">
                        {list.slice(0, 2).map((s) => (
                          <span
                            key={s.id}
                            className={`block px-1 py-0.5 text-[10px] font-semibold leading-tight rounded truncate ${
                              s.type === "educational"
                                ? "bg-violet-100 text-violet-800"
                                : "bg-emerald-100 text-emerald-800"
                            } ${past ? "opacity-70" : ""}`}
                          >
                            {hhmm(s.start_at)}
                          </span>
                        ))}
                        {list.length > 2 && (
                          <span className="block text-[10px] font-bold text-slate-400">
                            +{list.length - 2}
                          </span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* The selected day, spelled out. The cells only have room for a
                  time, and a club admin opening this wants the whole entry. */}
              <div className="pt-4 mt-4 border-t border-slate-100">
                <div className="flex items-center gap-3">
                  <p className="text-sm font-bold text-slate-700">
                    {selected
                      ? new Date(`${selected}T12:00:00`).toLocaleDateString("ro-RO", {
                          weekday: "long",
                          day: "numeric",
                          month: "long",
                        })
                      : "Alege o zi"}
                  </p>
                  {onAddForDay && selected && (
                    <button
                      onClick={() => onAddForDay(selected)}
                      className="ml-auto text-sm font-semibold text-indigo-600 hover:text-indigo-700"
                    >
                      + Adaugă în ziua asta
                    </button>
                  )}
                </div>
                {selectedSessions.length === 0 ? (
                  <p className="mt-2 text-sm text-slate-400">Niciun antrenament în ziua asta.</p>
                ) : (
                  <ul className="mt-2 divide-y divide-slate-100">
                    {selectedSessions.map((s) => (
                      <li key={s.id} className="flex flex-wrap items-center gap-2 py-2 text-sm">
                        <span className="font-bold tabular-nums text-slate-800">
                          {hhmm(s.start_at)}–{hhmm(s.end_at)}
                        </span>
                        <span className="text-slate-700">{s.title}</span>
                        <span
                          className={`px-2 py-0.5 text-[11px] font-bold rounded-full ${
                            s.type === "educational"
                              ? "bg-violet-100 text-violet-700"
                              : "bg-emerald-100 text-emerald-700"
                          }`}
                        >
                          {s.type === "educational" ? "educațional" : "sportiv"}
                        </span>
                        <span className="ml-auto text-xs text-slate-500">
                          {[
                            s.coach?.full_name || s.coach?.email,
                            s.location?.name || s.location_text,
                          ]
                            .filter(Boolean)
                            .join(" · ") || "—"}
                        </span>
                        {dayKey(new Date(s.start_at)) < todayKey && (
                          <span
                            className={`text-[11px] font-bold ${
                              (s._count?.attendance || 0) > 0 ? "text-emerald-600" : "text-amber-600"
                            }`}
                          >
                            {(s._count?.attendance || 0) > 0 ? "pontat" : "fără prezență"}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="flex items-center gap-4 pt-3 mt-3 text-xs border-t border-slate-100">
                <span className="inline-flex items-center gap-1.5 text-slate-500">
                  <span className="w-3 h-3 rounded bg-emerald-100 border border-emerald-200" /> sportiv
                </span>
                <span className="inline-flex items-center gap-1.5 text-slate-500">
                  <span className="w-3 h-3 border rounded bg-violet-100 border-violet-200" /> educațional
                </span>
                {loading && <span className="text-slate-400">se încarcă...</span>}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
